import {
  type StudyCreateDto,
  StudyCreateDtoClassificationCeilingEnum,
  StudyCreateDtoClassificationLevelEnum,
  type StudySelectDto,
  StudySelectDtoClassificationLevelEnum,
  StudyUpdateDtoClassificationLevelEnum,
} from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { ConfirmationModal } from '@components/shared/confirm-delete-modal.component';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useArchiveMeiaStudy, useCreateMeiaStudy, useUpdateMeiaStudy } from '@queries/meia.query';
import { type MeiaStudyUpdateFormValues, meiaStudyCreateSchema, meiaStudyUpdateSchema } from '@schemas/meia.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { getClassificationLevelLabel } from '@utils/meia-enum.util';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';
import { STUDY_ADVERSARY_PROFILE_OPTIONS, STUDY_TIME_HORIZON_OPTIONS } from './meia-study-options.util';

type MeiaStudyCreateModalProps = {
  getStudyPath?: (studyId: string) => string;
  isOpen: boolean;
  onClose: () => void;
};

const classificationCeilingOptions = Object.values(StudyCreateDtoClassificationCeilingEnum).map((value) => ({
  label: getClassificationLevelLabel(value),
  value,
}));

const classificationLevelOptions = Object.values(StudyCreateDtoClassificationLevelEnum).map((value) => ({
  label: getClassificationLevelLabel(value),
  value,
}));

const updateClassificationLevelOptions = Object.values(StudyUpdateDtoClassificationLevelEnum).map((value) => ({
  label: getClassificationLevelLabel(value),
  value,
}));

const studySelectToUpdateClassificationLevel: Record<
  StudySelectDtoClassificationLevelEnum,
  StudyUpdateDtoClassificationLevelEnum
> = {
  [StudySelectDtoClassificationLevelEnum.CUI]: StudyUpdateDtoClassificationLevelEnum.CUI,
  [StudySelectDtoClassificationLevelEnum.SECRET]: StudyUpdateDtoClassificationLevelEnum.SECRET,
  [StudySelectDtoClassificationLevelEnum.TOP_SECRET]: StudyUpdateDtoClassificationLevelEnum.TOP_SECRET,
  [StudySelectDtoClassificationLevelEnum.UNCLASSIFIED]: StudyUpdateDtoClassificationLevelEnum.UNCLASSIFIED,
};

const studyCreateDefaultValues: StudyCreateDto = {
  adversaryProfiles: [],
  classificationCeiling: StudyCreateDtoClassificationCeilingEnum.UNCLASSIFIED,
  classificationLevel: StudyCreateDtoClassificationLevelEnum.UNCLASSIFIED,
  description: '',
  name: '',
  timeHorizon: null,
};

export const MeiaStudyCreateModal: FC<MeiaStudyCreateModalProps> = (props) => {
  const { getStudyPath = (studyId: string) => `/meia/${studyId}/overview`, isOpen, onClose } = props;
  const navigate = useNavigate();
  const { isPending, mutateAsync: createStudy } = useCreateMeiaStudy();

  const form = useAppForm({
    defaultValues: studyCreateDefaultValues,
    onSubmit: async ({ value }) => {
      const validationResult = meiaStudyCreateSchema.safeParse(value);

      if (!validationResult.success) {
        errorToast({ description: 'Please fill in all required fields correctly.' });
        return;
      }

      const { description, ...rest } = validationResult.data;
      const payload: StudyCreateDto = {
        ...rest,
        description: description?.trim() ? description.trim() : null,
      };

      const study = await createStudy(payload);
      successToast({ description: 'MEIA study created successfully.' });
      form.reset();
      onClose();
      navigate(getStudyPath(study.id));
    },
    onSubmitInvalid: createFormInvalidHandler<typeof studyCreateDefaultValues>(),
    validators: {
      onSubmit: meiaStudyCreateSchema,
    },
  });

  const handleClose = (): void => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      centered
      footer={{
        rightButton: {
          children: 'Create Study',
          disabled: isPending,
          loading: isPending,
          onClick: () => void form.handleSubmit(),
          type: 'button',
        },
      }}
      header={{ title: 'New Study' }}
      isOpen={isOpen}
      onClose={handleClose}
      width="720px"
    >
      <form
        {...MEIA_AUTOFILL_IGNORE_PROPS}
        className="grid gap-4 md:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <form.AppField name="name">
            {(field) => (
              <field.TextField
                {...MEIA_AUTOFILL_IGNORE_PROPS}
                className="md:col-span-2"
                label="Name"
                placeholder="e.g., Contested Logistics MEIA"
                required
              />
            )}
          </form.AppField>
          <form.AppField name="description">
            {(field) => (
              <field.TextAreaField
                className="md:col-span-2"
                label="Description"
                placeholder="Briefly describe study purpose and operational context."
                rows={3}
              />
            )}
          </form.AppField>
          <form.AppField name="classificationLevel">
            {(field) => (
              <field.SelectField
                label="Classification Level"
                options={classificationLevelOptions}
                required
              />
            )}
          </form.AppField>
          <form.AppField name="classificationCeiling">
            {(field) => (
              <field.SelectField
                label="Classification Ceiling"
                options={classificationCeilingOptions}
                required
              />
            )}
          </form.AppField>
          <form.AppField name="adversaryProfiles">
            {(field) => (
              <field.SelectMultipleField
                className="md:col-span-2"
                label="Adversary Profile"
                options={STUDY_ADVERSARY_PROFILE_OPTIONS}
                placeholder="Select adversary profiles"
              />
            )}
          </form.AppField>
          <form.AppField name="timeHorizon">
            {(field) => (
              <field.SelectField
                clearable
                className="md:col-span-2"
                label="Time Horizon"
                options={STUDY_TIME_HORIZON_OPTIONS}
                placeholder="Select time horizon"
              />
            )}
          </form.AppField>
        </form.AppForm>
      </form>
    </Modal>
  );
};

type MeiaStudyEditModalProps = {
  isOpen: boolean;
  onClose: () => void;
  study: StudySelectDto;
};

export const MeiaStudyEditModal: FC<MeiaStudyEditModalProps> = (props) => {
  const { isOpen, onClose, study } = props;
  const [isConfirmingArchive, setIsConfirmingArchive] = useState(false);
  const { isPending, mutateAsync: updateStudy } = useUpdateMeiaStudy();
  const { isPending: isArchiving, mutateAsync: archiveStudy } = useArchiveMeiaStudy();

  const defaultValues: MeiaStudyUpdateFormValues = useMemo(
    () => ({
      adversaryProfiles: study.adversaryProfiles ?? [],
      classificationLevel: studySelectToUpdateClassificationLevel[study.classificationLevel],
      description: study.description ?? '',
      name: study.name,
      timeHorizon: study.timeHorizon ?? null,
    }),
    [study.adversaryProfiles, study.classificationLevel, study.description, study.name, study.timeHorizon],
  );

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const validationResult = meiaStudyUpdateSchema.safeParse(value);

      if (!validationResult.success) {
        errorToast({ description: 'Please fill in all required fields correctly.' });
        return;
      }

      const { description, ...rest } = validationResult.data;
      await updateStudy({
        ...rest,
        description: description?.trim() ? description.trim() : null,
        studyId: study.id,
      });
      successToast({ description: 'Study updated successfully.' });
      onClose();
    },
    onSubmitInvalid: createFormInvalidHandler<typeof defaultValues>(),
    validators: {
      onSubmit: meiaStudyUpdateSchema,
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    form.reset(defaultValues);
  }, [defaultValues, form, isOpen]);

  const handleClose = (): void => {
    form.reset(defaultValues);
    onClose();
  };

  const handleConfirmArchive = async (): Promise<void> => {
    await archiveStudy({ studyId: study.id });
    successToast({ description: 'Study archived.' });
    onClose();
  };

  return (
    <Modal
      centered
      footer={{
        leftButton: study.canDeleteStudy
          ? {
              children: isArchiving ? 'Archiving…' : 'Archive',
              disabled: isArchiving || isPending,
              loading: isArchiving,
              onClick: () => setIsConfirmingArchive(true),
              type: 'button',
              variant: 'outline',
            }
          : undefined,
        rightButton: {
          children: 'Save Study',
          disabled: isPending,
          loading: isPending,
          onClick: () => void form.handleSubmit(),
          type: 'button',
        },
      }}
      header={{ title: 'Edit Study' }}
      isOpen={isOpen}
      onClose={handleClose}
      width="720px"
    >
      <form
        {...MEIA_AUTOFILL_IGNORE_PROPS}
        className="grid gap-4 md:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <form.AppField name="name">
            {(field) => (
              <field.TextField
                {...MEIA_AUTOFILL_IGNORE_PROPS}
                className="md:col-span-2"
                label="Name"
                placeholder="e.g., Contested Logistics MEIA"
                required
              />
            )}
          </form.AppField>
          <form.AppField name="description">
            {(field) => (
              <field.TextAreaField
                className="md:col-span-2"
                label="Description"
                placeholder="Briefly describe study purpose and operational context."
                rows={3}
              />
            )}
          </form.AppField>
          <form.AppField name="classificationLevel">
            {(field) => (
              <field.SelectField
                label="Classification Level"
                options={updateClassificationLevelOptions}
                required
              />
            )}
          </form.AppField>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Classification Ceiling</label>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {getClassificationLevelLabel(study.classificationCeiling)}
            </div>
            <span className="text-xs text-gray-500">Set at study creation and cannot be changed.</span>
          </div>
          <form.AppField name="adversaryProfiles">
            {(field) => (
              <field.SelectMultipleField
                className="md:col-span-2"
                label="Adversary Profile"
                options={STUDY_ADVERSARY_PROFILE_OPTIONS}
                placeholder="Select adversary profiles"
              />
            )}
          </form.AppField>
          <form.AppField name="timeHorizon">
            {(field) => (
              <field.SelectField
                clearable
                className="md:col-span-2"
                label="Time Horizon"
                options={STUDY_TIME_HORIZON_OPTIONS}
                placeholder="Select time horizon"
              />
            )}
          </form.AppField>
        </form.AppForm>
      </form>
      <ConfirmationModal
        confirmLabel="Archive Study"
        description="Archiving hides this study from the default studies list. You can restore it later from the Archived filter."
        isOpen={isConfirmingArchive}
        isPending={isArchiving}
        onConfirm={handleConfirmArchive}
        setIsOpen={setIsConfirmingArchive}
        title="Archive this study?"
        variant="destructive"
      />
    </Modal>
  );
};
